/**
 * InAppConsole — In-app debug overlay for the myIntelli Clock kiosk.
 *
 * Design: in-app-console design.md §Overlay z-index (9998), §Virtualization (none),
 *         §Teardown, §Data Flow
 * Spec: IAC-11 (clear), IAC-10 (route-change survival), collapse/expand toggle.
 *
 * Rules (non-negotiable per myintelli-clock-hex skill):
 *  - useTheme() → { T, C } for ALL brand colors — NO hardcoded brand hex
 *  - Inline style={{}} for EVERY color, shadow, border
 *  - NO portals — fixed position, mounted inline in the app tree (above RouterProvider)
 *  - position: fixed; zIndex: 9998 — locked decision (below DeviceEnrollBanner 9999)
 *  - Collapsed by default when feature flag turns ON
 *  - Autoscroll to newest entry unless user has scrolled up (scroll-lock)
 *  - No virtualization — cap is 500 (ring buffer), scroll container handles it
 *  - Per-type styling: error/global-error/rejection = red, warn = amber, http = monospace pre, log = T.text
 */
import { useRef, useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useConsoleCapture, store } from '@/shared/devtools/store';
import { useTheme } from '@/shared/ui/theme/ThemeProvider';
import type { Entry, EntryType } from '@/shared/devtools/ringBuffer';

// ─── Constants ────────────────────────────────────────────────────────────────

/**
 * Warning/amber color used exclusively for the dev console warn entries.
 * Not a brand color — devtools-specific. Using inline constant is acceptable
 * for a developer-facing overlay that is not part of the main UI token system.
 */
const WARN_COLOR = '#F59E0B';

// ─── Helpers ─────────────────────────────────────────────────────────────────

function entryColor(type: EntryType, danger: string, textColor: string): string {
  switch (type) {
    case 'error':
    case 'global-error':
    case 'rejection':
      return danger;
    case 'warn':
      return WARN_COLOR;
    case 'http':
    case 'log':
    default:
      return textColor;
  }
}

// ─── Sub-components ───────────────────────────────────────────────────────────

interface EntryRowProps {
  entry: Entry;
  index: number;
  danger: string;
  textColor: string;
  textMuted: string;
  border: string;
}

function EntryRow({ entry, index, danger, textColor, textMuted, border }: EntryRowProps) {
  const color = entryColor(entry.type, danger, textColor);
  const ts = new Date(entry.t).toISOString().slice(11, 23); // HH:MM:SS.mmm

  return (
    <div
      data-testid={`console-entry-${index}`}
      data-console-type={entry.type}
      style={{
        color,
        borderBottom: `1px solid ${border}`,
        padding: '4px 8px',
        fontSize: 11,
        lineHeight: 1.5,
        display: 'flex',
        flexDirection: 'column',
        gap: 1,
      }}
    >
      <span style={{ color: textMuted, fontSize: 10 }}>{ts} [{entry.type}]</span>
      {entry.type === 'http' ? (
        <pre
          style={{
            fontFamily: 'monospace',
            margin: 0,
            whiteSpace: 'pre-wrap',
            wordBreak: 'break-all',
            fontSize: 11,
            color,
          }}
        >
          {entry.payload}
        </pre>
      ) : (
        <span style={{ wordBreak: 'break-all' }}>{entry.payload}</span>
      )}
    </div>
  );
}

// ─── Main component ───────────────────────────────────────────────────────────

export function InAppConsole() {
  const { t } = useTranslation('common');
  const { C, T } = useTheme();
  const entries = useConsoleCapture();

  const [expanded, setExpanded] = useState(false);

  // Autoscroll refs
  const scrollRef = useRef<HTMLDivElement>(null);
  const isScrollLockedRef = useRef(false);

  // Scroll-lock: if user scrolls up, lock autoscroll; resume when at bottom.
  const handleScroll = useCallback(() => {
    const el = scrollRef.current;
    if (!el) return;
    const atBottom = el.scrollHeight - el.scrollTop - el.clientHeight < 40;
    isScrollLockedRef.current = !atBottom;
  }, []);

  // Autoscroll to bottom on new entries (unless scroll-locked).
  useEffect(() => {
    if (!expanded || isScrollLockedRef.current) return;
    const el = scrollRef.current;
    if (el) {
      el.scrollTop = el.scrollHeight;
    }
  }, [entries, expanded]);

  const handleClear = () => {
    store.clear();
  };

  const toggleExpand = () => {
    setExpanded((prev) => !prev);
  };

  const handleCollapse = () => {
    setExpanded(false);
  };

  return (
    <div
      data-testid="in-app-console-root"
      style={{
        position: 'fixed',
        bottom: 16,
        right: 16,
        zIndex: 9998,
        fontFamily: 'inherit',
        pointerEvents: 'auto',
      }}
    >
      {/* ── Collapsed pill ──────────────────────────────────────────────── */}
      {!expanded && (
        <button
          type="button"
          data-testid="in-app-console-pill"
          aria-label={t('devConsole.title')}
          onClick={toggleExpand}
          style={{
            background: T.surfaceBg,
            border: `1px solid ${T.border}`,
            borderRadius: 999,
            boxShadow: T.shadow,
            padding: '4px 12px',
            fontSize: 11,
            fontWeight: 600,
            color: C.primary,
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            gap: 6,
          }}
        >
          {/* Small terminal icon */}
          <svg
            width={12}
            height={12}
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <polyline points="4 17 10 11 4 5" />
            <line x1="12" y1="19" x2="20" y2="19" />
          </svg>
          {t('devConsole.collapsedPillLabel')}
          {entries.length > 0 && (
            <span
              style={{
                background: C.danger,
                color: '#FFF',
                borderRadius: 999,
                fontSize: 9,
                fontWeight: 700,
                padding: '0 5px',
                lineHeight: '14px',
              }}
            >
              {entries.length}
            </span>
          )}
        </button>
      )}

      {/* ── Expanded drawer ─────────────────────────────────────────────── */}
      {expanded && (
        <div
          data-testid="in-app-console-drawer"
          style={{
            width: 360,
            maxHeight: 460,
            background: T.surfaceBg,
            border: `1px solid ${T.border}`,
            borderRadius: 10,
            boxShadow: T.shadow,
            display: 'flex',
            flexDirection: 'column',
            overflow: 'hidden',
          }}
        >
          {/* Header */}
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              padding: '8px 10px',
              borderBottom: `1px solid ${T.border}`,
              gap: 8,
              flexShrink: 0,
            }}
          >
            <svg
              width={13}
              height={13}
              viewBox="0 0 24 24"
              fill="none"
              stroke={C.primary}
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <polyline points="4 17 10 11 4 5" />
              <line x1="12" y1="19" x2="20" y2="19" />
            </svg>
            <span
              style={{
                flex: 1,
                fontSize: 12,
                fontWeight: 700,
                color: T.text,
                letterSpacing: '-.01em',
              }}
            >
              {t('devConsole.title')}
            </span>

            {/* Clear button */}
            <button
              type="button"
              data-testid="in-app-console-clear-btn"
              onClick={handleClear}
              style={{
                background: `${T.textMuted}22`,
                border: 'none',
                borderRadius: 4,
                padding: '2px 8px',
                fontSize: 10,
                color: T.textMuted,
                cursor: 'pointer',
              }}
            >
              {t('devConsole.clearButton')}
            </button>

            {/* Collapse button */}
            <button
              type="button"
              data-testid="in-app-console-collapse-btn"
              aria-label={t('devConsole.collapseLabel')}
              onClick={handleCollapse}
              style={{
                background: 'none',
                border: 'none',
                padding: 4,
                cursor: 'pointer',
                color: T.textMuted,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                borderRadius: 4,
              }}
            >
              <svg
                width={12}
                height={12}
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2.5"
                strokeLinecap="round"
              >
                <line x1="18" y1="6" x2="6" y2="18" />
                <line x1="6" y1="6" x2="18" y2="18" />
              </svg>
            </button>
          </div>

          {/* Entry list */}
          <div
            ref={scrollRef}
            onScroll={handleScroll}
            style={{
              flex: 1,
              overflowY: 'auto',
              overflowX: 'hidden',
            }}
          >
            {entries.length === 0 ? (
              <div
                data-testid="in-app-console-empty"
                style={{
                  padding: 20,
                  textAlign: 'center',
                  color: T.textMuted,
                  fontSize: 11,
                }}
              >
                {t('devConsole.emptyState')}
              </div>
            ) : (
              entries.map((entry, i) => (
                <EntryRow
                  key={`${entry.t}-${i}`}
                  entry={entry}
                  index={i}
                  danger={C.danger}
                  textColor={T.text}
                  textMuted={T.textMuted}
                  border={T.border}
                />
              ))
            )}
          </div>
        </div>
      )}
    </div>
  );
}
